import type { LabelGroupDetailQueryVariables } from '~settings/api/queries/labelGroup';
import type { UserRolesQueryVariables } from '~settings/api/queries/userRoles';

import type { LabelDetailQueryVariables } from '../queries/label';
import type { LabelGroupInfiniteListQueryVariables } from '../queries/labelGroupInfiniteList';
import type { LabelGroupListQueryVariables } from '../queries/labelGroupList';
import type { LabelInfiniteListQueryVariables } from '../queries/labelInfiniteList';
import type { LabelListQueryVariables } from '../queries/labelList';
import type { OktaUserQueryVariables } from '../queries/okta-user';
import type { PresentationLabelInfiniteListQueryVariables } from '../queries/presentationLabelInfiniteList';
import type { PresentationLabelListQueryVariables } from '../queries/presentationLabelList';
import type { UserDetailQueryVariables } from '../queries/user';
import type { UserListQueryVariables } from '../queries/userList';
import type { UserTypesQueryVariables } from '../queries/userTypes';

const user = () => [{ entity: 'user' }] as const;

const userLists = () => [{ ...user()[0], scope: 'list' }] as const;
const userList = (variables: UserListQueryVariables) =>
  [{ ...userLists()[0], variables }] as const;

const userDetails = () => [{ ...user()[0], scope: 'detail' }] as const;
const userDetail = (variables: UserDetailQueryVariables) =>
  [{ ...userDetails()[0], variables }] as const;
const oktaUser = (variables: OktaUserQueryVariables) => [variables];

export const UserQueryKeys = {
  all: user,
  lists: userLists,
  list: userList,
  details: userDetails,
  detail: userDetail,
  oktaUser,
};

const label = () => [{ entity: 'label' }] as const;

const labelLists = () => [{ ...label()[0], scope: 'list' }] as const;
const labelList = (variables: LabelListQueryVariables) =>
  [{ ...labelLists()[0], variables }] as const;
const labelInfiniteList = (variables: LabelInfiniteListQueryVariables) =>
  [{ ...labelLists()[0], variables, infinite: true }] as const;

const labelDetails = () => [{ ...label()[0], scope: 'detail' }] as const;
const labelDetail = (variables: LabelDetailQueryVariables) =>
  [{ ...labelDetails()[0], variables }] as const;

export const LabelQueryKeys = {
  all: label,
  lists: labelLists,
  list: labelList,
  infiniteList: labelInfiniteList,
  details: labelDetails,
  detail: labelDetail,
};

const distributor = () => [{ entity: 'distributor' }] as const;
const distributorLists = () =>
  [{ ...distributor()[0], scope: 'list' }] as const;
const distributorList = () => [{ ...distributorLists()[0] }] as const;

export const DistributorQueryKeys = {
  all: distributor,
  lists: distributorLists,
  list: distributorList,
};

const presentationLabel = () => [{ entity: 'presentationLabel' }] as const;
const presentationLabelLists = () =>
  [{ ...presentationLabel()[0], scope: 'list' }] as const;
const presentationLabelList = (
  variables: PresentationLabelListQueryVariables,
) => [{ ...presentationLabelLists()[0], variables }] as const;
const presentationLabelInfiniteList = (
  variables: PresentationLabelInfiniteListQueryVariables,
) => [{ ...presentationLabelLists()[0], variables, infinite: 'true' }] as const;

export const PresentationLabelQueryKeys = {
  all: presentationLabel,
  lists: presentationLabelLists,
  list: presentationLabelList,
  infiniteList: presentationLabelInfiniteList,
};

const labelGroup = () => [{ entity: 'labelGroup' }] as const;

const labelGroupLists = () => [{ ...labelGroup()[0], scope: 'list' }] as const;
const labelGroupList = (variables: LabelGroupListQueryVariables) =>
  [{ ...labelGroupLists()[0], variables }] as const;
const labelGroupInfiniteList = (
  variables: LabelGroupInfiniteListQueryVariables,
) => [{ ...labelGroupLists()[0], variables, infinite: 'true' }] as const;

const labelGroupDetails = () =>
  [{ ...labelGroup()[0], scope: 'detail' }] as const;
const labelGroupDetail = (variables: LabelGroupDetailQueryVariables) =>
  [{ ...labelGroupDetails()[0], variables }] as const;

export const LabelGroupQueryKeys = {
  all: labelGroup,
  lists: labelGroupLists,
  list: labelGroupList,
  infiniteList: labelGroupInfiniteList,
  detail: labelGroupDetail,
};

export const OrganizationQueryKeys = {
  list: () => [{ entity: 'organization', scope: 'list' }] as const,
};

export const DealOwnerQueryKeys = {
  list: () => [{ entity: 'dealOwner', scope: 'list' }] as const,
};

export const CountryGroupQueryKeys = {
  list: () => [{ entity: 'countryGroup', scope: 'list' }] as const,
};

export const RoleQueryKeys = {
  roles: (variables: UserRolesQueryVariables) =>
    [{ entity: 'role', scope: 'list', variables }] as const,
  types: (variables: UserTypesQueryVariables) =>
    [{ entity: 'role', scope: 'types', variables }] as const,
};
